import React from "react";
import {
  normalizeRiskScale,
  getSignificanceThreshold,
} from "../constants/riskAnalysisMatrix";
import {
  riskScaleBannerPillClasses,
  riskLevelPillClasses,
  getRiskSeverityReferenceTiers,
} from "../constants/leadAdvisorRiskSeverityReference";

/**
 * @param {{ project?: object, contentSealedReadOnly?: boolean }} props
 */
export function LeadAdvisorRiskScaleAndKpisPanel({
  project,
  contentSealedReadOnly = false,
}) {
  void contentSealedReadOnly;
  const profile = project?.profile && typeof project.profile === "object" ? project.profile : {};
  const riskOnboarding = profile.riskOnboarding;
  const hasRiskBlob =
    riskOnboarding &&
    typeof riskOnboarding === "object" &&
    String(riskOnboarding.riskScale || "").trim() !== "";
  // No default scale: when onboarding has not set one, nothing is derived or shown.
  const scale = hasRiskBlob ? normalizeRiskScale(riskOnboarding.riskScale) : null;
  const n = scale == null ? null : Number(scale);
  const threshold = scale == null ? null : getSignificanceThreshold(scale);
  const tierRows = scale == null ? [] : getRiskSeverityReferenceTiers(scale);
  const pillColours = scale == null ? [] : riskScaleBannerPillClasses(scale);

  return (
    <div className="space-y-8">
      {!hasRiskBlob ? (
        <div data-testid="risk-scale-not-set" className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-8 shadow-xl">
          <h3 className="text-sm font-black uppercase tracking-tight text-[color:var(--color-text-hero)]">Risk assessment scale</h3>
          <div className="mt-4 rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] px-5 py-6">
            <p className="text-sm font-bold text-[color:var(--color-text-hero)]">No risk scale set up yet</p>
            <p className="mt-1 text-sm font-medium text-[color:var(--color-text-muted-dark)]">
              Configure it in <strong>Project Onboarding → Risk scale</strong>; the scale and its tier table appear here once it is set.
            </p>
          </div>
        </div>
      ) : (
      <div className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-8 shadow-xl">
        <div className="flex flex-col gap-6 xl:flex-row xl:items-start xl:justify-between">
          <div className="max-w-xl">
            <h3 className="text-sm font-black uppercase tracking-tight text-[color:var(--color-text-hero)]">
              Risk assessment scale and KPIs
            </h3>
            <div className="mt-3 inline-flex rounded-lg border border-[color:var(--color-accent-fill)] bg-[color:var(--color-accent-fill)] px-3 py-1.5 font-mono text-xs font-black uppercase tracking-wider text-white/80">
              {n}-point scale
            </div>
            <p className="mt-4 text-sm font-medium leading-relaxed text-[color:var(--color-text-muted-dark)]">
              Configured in <span className="font-semibold text-[color:var(--color-text-hero)]">Step 3 - Project Onboarding</span> (Risk
              scale). Org-wide significance threshold for material risk alignment:{" "}
              <span className="font-mono font-semibold text-[color:var(--color-text-hero)]">{threshold}</span>{" "}
              (Severity × Likelihood × Consequence × Control strength product benchmark).
            </p>
          </div>
          <div
            className="flex shrink-0 flex-wrap content-start items-center justify-end gap-1.5 sm:max-w-md xl:justify-end"
            aria-label={`${n}-point risk scale colour pills`}
          >
            {pillColours.map((cls, i) => (
              <span
                key={`pill-${cls}-${i}`}
                title={`Band ${i + 1}`}
                className={`inline-block size-9 rounded-xl border border-white/30 shadow-inner sm:size-10 ${cls}`}
              />
            ))}
          </div>
        </div>

        <p className="mt-4 text-xs leading-relaxed text-[color:var(--color-text-muted)]">
          The cheat-sheet scores below are read-only summaries of{" "}
          <span className="font-semibold text-[color:var(--color-text-muted-dark)]">likelihood and impact tiers</span> for each level on your
          selected numeric scale - not a recomputation of onboarding&apos;s Severity×Likelihood×Consequence×Control
          matrix cells.
        </p>

        <div className="mt-8 overflow-x-auto rounded-2xl border border-[color:var(--color-border)] shadow-sm">
          <table className="w-full min-w-[880px] text-left text-sm">
            <thead>
              <tr className="bg-[color:var(--color-accent-fill)] text-[10px] font-black uppercase tracking-[0.15em] text-white/80">
                <th className="px-4 py-4 w-16">Score</th>
                <th className="px-4 py-4">Likelihood</th>
                <th className="px-4 py-4">Impact</th>
                <th className="px-4 py-4 min-w-[120px]">Risk level</th>
                <th className="px-4 py-4 min-w-[280px]">Description</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[color:var(--color-border)]">
              {tierRows.map((row) => (
                <tr key={row.score} className="font-medium text-[color:var(--color-text-hero)]">
                  <td className="px-4 py-3 font-mono text-[color:var(--color-text-muted-dark)]">{row.score}</td>
                  <td className="px-4 py-3">{row.likelihood}</td>
                  <td className="px-4 py-3">{row.impact}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex rounded-lg border px-2.5 py-0.5 text-[10px] font-black uppercase tracking-widest ${riskLevelPillClasses(row.pillKey)}`}
                    >
                      {row.riskLevel}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm font-normal leading-relaxed text-[color:var(--color-text-muted-dark)]">{row.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      )}

      <div className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-8 shadow-xl">
        <h3 className="text-sm font-black uppercase tracking-tight text-[color:var(--color-text-hero)]">KPI master</h3>
        <div data-testid="kpi-not-available" className="mt-4 rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] px-5 py-6">
          <p className="text-sm font-bold text-[color:var(--color-text-hero)]">KPI tracking is not available yet</p>
          <p className="mt-1 max-w-3xl text-sm font-medium text-[color:var(--color-text-muted-dark)]">
            KPIs appear here once Evidence, Controls, training and incident data supply real values for them.
          </p>
        </div>
      </div>
    </div>
  );
}
